import { useCallback, useMemo } from "react";
import { usePreference } from "@/contexts/PreferencesContext";
import { useRootLoader } from "@/hooks/useRootLoader";
import type { MyDepartmentItem } from "@shared-domain/unizo/contract";

/**
 * いま「どの部署として」見ているか（PRODUCT_DEV-917）
 *
 * 兼務している人が、記録する先・選べるプリセット・一覧の既定を、部署ごとに切り替えるための
 * 唯一の入口。選んだ部署は個人設定（サーバ）に残るので、端末を変えても同じ部署で開く。
 *
 * ## 出す条件は「その人が2つ以上に所属していること」だけ
 *
 * ⚠ **機能フラグを付けていない。** 1部署の人と未所属の人には `canSwitch: false` が返り、
 * 画面は今までとまったく同じになる。いま兼務している人はごく少数なので、フラグで塞ぐより
 * 「複数あるときだけ出る」ほうが、塞ぎ忘れも外し忘れも起きない。
 *
 * ## 所属から外れた部署を選んだまま残しうる
 *
 * 異動や部署の削除で、覚えている `activeDeptId` が**いまの所属に無い**ことがある。
 * そのときは黙って先頭の所属へ落とす。**エラーにしない**——本人には何が起きたか分からないし、
 * 落とし先は必ずあるため。
 */
export interface ActiveDepartment {
  /** いま見ている部署。未所属なら null。 */
  active: MyDepartmentItem | null;
  /** 所属している部署の全部（並びはサーバが決めた順）。 */
  all: MyDepartmentItem[];
  /** 切替を出すか（2つ以上に所属しているときだけ true）。 */
  canSwitch: boolean;
  /** 部署を選ぶ。個人設定に残る。 */
  setActive: (deptId: string) => void;
}

export const useActiveDepartment = (): ActiveDepartment => {
  const { myDepartment, myDepartments } = useRootLoader();
  const [activeDeptId, setActiveDeptId] = usePreference("activeDeptId", "");

  /**
   * 所属の一覧。**古いサーバは `departments` を返さない**ので、その場合は
   * 単数の `department` を1件の配列として扱う（画面は「1部署の人」として振る舞う）。
   */
  const all = useMemo<MyDepartmentItem[]>(() => {
    if (myDepartments && myDepartments.length > 0) return myDepartments;
    return myDepartment ? [myDepartment] : [];
  }, [myDepartments, myDepartment]);

  const active = useMemo<MyDepartmentItem | null>(() => {
    if (all.length === 0) return null;
    return all.find((d) => d.deptId === activeDeptId) ?? all[0];
  }, [all, activeDeptId]);

  const setActive = useCallback(
    (deptId: string) => setActiveDeptId(deptId),
    [setActiveDeptId],
  );

  return { active, all, canSwitch: all.length > 1, setActive };
};
